// frontend/src/components/TwoFactorLogin.js
// Two-Factor Authentication login verification component

import React, { useState } from 'react';
import api from '../services/api';

const TwoFactorLogin = ({ onVerified, onCancel }) => {
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showBackupCode, setShowBackupCode] = useState(false);

  const handleVerify = async () => {
    if (!token.trim()) {
      setError('Please enter your 2FA code');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await api.post('/2fa/validate', {
        token: token.trim()
      });

      if (response.data.success) {
        onVerified?.(response.data);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid 2FA code');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleVerify();
    }
  };

  return (
    <div className="two-factor-login">
      <div className="verification-form">
        <h3>Two-Factor Authentication</h3>
        <p>
          {showBackupCode 
            ? 'Enter one of your backup codes to continue:' 
            : 'Enter the 6-digit code from your authenticator app:'
          }
        </p>
        
        <input
          type="text"
          value={token}
          onChange={(e) => setToken(showBackupCode 
            ? e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8)
            : e.target.value.replace(/\D/g, '').slice(0, 6)
          )}
          onKeyPress={handleKeyPress}
          placeholder={showBackupCode ? 'XXXXXXXX' : '000000'}
          className="verification-input"
          maxLength={showBackupCode ? 8 : 6}
          disabled={loading}
          autoFocus
        />
        
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}
        
        <div className="actions">
          <button 
            onClick={handleVerify}
            disabled={loading || !token.trim() || 
              (!showBackupCode && token.length !== 6) ||
              (showBackupCode && token.length !== 8)}
            className="btn btn-primary"
          >
            {loading ? 'Verifying...' : 'Verify'}
          </button>
          <button 
            onClick={onCancel}
            className="btn btn-secondary"
            disabled={loading}
          >
            Cancel
          </button>
        </div>

        <div className="backup-toggle">
          <button
            type="button"
            onClick={() => {
              setShowBackupCode(!showBackupCode);
              setToken('');
              setError(null);
            }}
            className="link-button"
            disabled={loading}
          >
            {showBackupCode 
              ? 'Use authenticator app instead' 
              : 'Use backup code instead'
            }
          </button>
        </div>
      </div>

      <style jsx>{`
        .two-factor-login {
          max-width: 400px;
          margin: 0 auto;
          padding: 20px;
          text-align: center;
        }

        .verification-form h3 {
          margin-bottom: 15px;
          color: #333;
        }

        .verification-form p {
          margin-bottom: 25px;
          color: #666;
          line-height: 1.5;
        }

        .verification-input {
          font-size: 24px;
          text-align: center;
          padding: 15px;
          border: 2px solid #ddd;
          border-radius: 8px;
          width: 200px;
          margin: 15px auto;
          font-family: monospace;
          letter-spacing: 3px;
        }

        .verification-input:focus {
          outline: none;
          border-color: #007bff;
        }

        .verification-input:disabled {
          background: #f5f5f5;
          opacity: 0.7;
        }

        .error-message {
          background: #f8d7da;
          color: #721c24;
          padding: 10px 15px;
          border-radius: 4px;
          margin: 15px 0;
          border: 1px solid #f5c6cb;
        }

        .actions {
          display: flex;
          gap: 10px;
          justify-content: center;
          margin: 25px 0;
        }

        .btn {
          padding: 10px 20px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          transition: background-color 0.2s;
        }

        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-primary {
          background: #007bff;
          color: white;
        }

        .btn-primary:hover:not(:disabled) {
          background: #0056b3;
        }

        .btn-secondary {
          background: #6c757d;
          color: white;
        }

        .btn-secondary:hover:not(:disabled) {
          background: #545b62;
        }

        .backup-toggle {
          margin-top: 20px;
          padding-top: 20px;
          border-top: 1px solid #eee;
        }

        .link-button {
          background: none;
          border: none;
          color: #007bff;
          text-decoration: underline;
          cursor: pointer;
          font-size: 14px;
          padding: 5px;
        }

        .link-button:hover:not(:disabled) {
          color: #0056b3;
        }

        .link-button:disabled {
          color: #ccc;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
};

export default TwoFactorLogin;